/**
 * Deploy-skew recovery.
 *
 * Photonium Insight is a long-lived SPA: users leave a conversation tab open
 * for hours (or overnight) while the agent runs optimization / tolerancing
 * jobs. When Vercel ships a new deployment, the hashed JS chunks this tab was
 * built against are eventually replaced. The next lazy import — e.g. the Plotly
 * chunk that loads the first time an analysis tab renders a chart — then 404s
 * with a ChunkLoadError.
 *
 * Vercel Skew Protection pins chunk URLs via `?dpl=` so an open tab keeps
 * hitting the deployment that served it, but only while that deployment stays
 * within the project's Maximum Age window and retention policy. Past that, the
 * 404 is unavoidable — and without recovery the user is left staring at a
 * "not found" page.
 *
 * A full page reload always recovers, because the fresh document request is
 * served the current production deployment. These helpers detect the chunk-load
 * failure and reload, bounded so a genuinely-broken current deployment (or a
 * context where the loop guard can't persist) can't spin forever.
 */

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

const RELOAD_GUARD_KEY = 'photoniumChunkReload';
// A reload "episode": consecutive failures within this window count together.
const RELOAD_EPISODE_MS = 30_000;
// How many times we auto-reload within one episode before giving up and handing
// off to the manual "Reload" button.
const MAX_RELOADS = 2;

/**
 * True if `error` (or any error it wraps via `.cause`) is a failed JS chunk /
 * dynamic-import load. `next/dynamic` and other wrappers can surface a generic
 * outer error with the real ChunkLoadError on `.cause`, so we walk the chain.
 */
export function isChunkLoadError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; current && depth < 4; depth++) {
    const { name, message } = current as { name?: string; message?: string };
    if (CHUNK_ERROR_RE.test(`${name ?? ''} ${message ?? ''}`)) return true;
    current = (current as { cause?: unknown }).cause;
  }
  return false;
}

/**
 * Reload to fetch the current deployment's assets. Returns true if a reload
 * was triggered, false if it was suppressed — the caller then shows a manual
 * "Reload" button instead of looping. Reload is suppressed when:
 *   - sessionStorage is unavailable (private mode, sandboxed iframe): the loop
 *     guard can't survive the reload, so auto-reloading could spin — fail safe
 *     and let the user drive the reload manually; or
 *   - we have already auto-reloaded MAX_RELOADS times this episode: the current
 *     deployment is genuinely broken, so stop and let the user decide.
 *
 * Shared by chunk-load recovery and version-skew recovery — both are the same
 * underlying episode (a deploy happened under an open tab), so they share one
 * guard rather than each getting their own reload budget.
 */
function guardedReload(): boolean {
  let store: Storage;
  try {
    store = window.sessionStorage;
    const probe = `${RELOAD_GUARD_KEY}:probe`;
    store.setItem(probe, '1');
    store.removeItem(probe);
  } catch {
    return false;
  }

  const now = Date.now();
  let attempts = 0;
  let episodeStart = now;
  try {
    const raw = store.getItem(RELOAD_GUARD_KEY);
    if (raw) {
      const prev = JSON.parse(raw) as { attempts?: number; start?: number };
      if (typeof prev.start === 'number' && now - prev.start < RELOAD_EPISODE_MS) {
        attempts = prev.attempts ?? 0;
        episodeStart = prev.start;
      }
    }
  } catch {
    // Corrupt/unreadable value — treat as a fresh episode.
  }

  if (attempts >= MAX_RELOADS) return false;

  store.setItem(
    RELOAD_GUARD_KEY,
    JSON.stringify({ attempts: attempts + 1, start: episodeStart }),
  );
  window.location.reload();
  return true;
}

/**
 * Reload to fetch the current deployment's assets, recovering from a chunk 404
 * after a deploy.
 *
 * Transient/offline network failures produce the same chunk-load errors and
 * land here too; a reload is a reasonable recovery for those as well, and the
 * per-episode cap bounds it.
 */
export function reloadForChunkError(): boolean {
  return guardedReload();
}

/** Reload to pick up the current deployment after version skew is detected.
 *  Same episode guard as chunk-error reloads: the two triggers share one cause. */
export function reloadForStaleDeploy(): boolean {
  return guardedReload();
}
